import { Component, output, ChangeDetectionStrategy } from "@angular/core";
import { SelectionModel } from "@angular/cdk/collections";
import { take } from "rxjs";
import { PropertySubType } from "../../../models/propertySubType";
import { PropertyType } from "../../../models/propertyType";
import { PropertyService } from "../../../services/PropertyService";

@Component({
  standalone: false,
  selector: 'app-property-type-filter',
  templateUrl: './property-type-filter.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./property-type-filter.component.scss']
})
export class PropertyTypeFilterComponent {
  readonly onPropertyTypeSelectionChange = output<PropertyType[] | undefined>();

  public propertyTypes: PropertyType[] = [];

  /** Checkbox state while the menu is open (not yet applied). */
  public subTypeSelection = new SelectionModel<number>(true, []);

  /** Last applied selection — what menuLabel/allTypesSelected reflect, and what we revert to on outside-click close. */
  private committedSubTypeIds: number[] = [];

  constructor(private propertyService: PropertyService) {
    this.propertyService.getPropertyTypes().pipe(take(1)).subscribe(propertyTypes => {
      this.propertyTypes = propertyTypes;
    });
  }

  public presetSelectedPropertyTypes(ptArray: PropertyType[] | undefined) {
    const ids = (ptArray ?? []).flatMap(pt => pt.subTypeIdList);
    this.committedSubTypeIds = [...ids];
    this.subTypeSelection = new SelectionModel<number>(true, ids);
  }

  get allTypesSelected(): boolean {
    return this.committedSubTypeIds.length === 0;
  }

  get menuLabel(): string {
    if (this.allTypesSelected) return "All Types";
    const typesWithSelection = this.propertyTypes.filter(pt =>
      pt.subTypeList.some(pst => this.committedSubTypeIds.includes(pst.id))
    );
    if (!typesWithSelection.length) return "All Types";
    const [first, ...rest] = typesWithSelection;
    return first.name + (rest.length > 0 ? ` (+${rest.length})` : "");
  }

  public isTypeChecked(pt: PropertyType): boolean {
    return pt.subTypeList.length > 0 && pt.subTypeList.every(pst => this.subTypeSelection.isSelected(pst.id));
  }

  public isTypeIndeterminate(pt: PropertyType): boolean {
    const selectedCount = pt.subTypeList.filter(pst => this.subTypeSelection.isSelected(pst.id)).length;
    return selectedCount > 0 && selectedCount < pt.subTypeList.length;
  }

  public isSubTypeChecked(pst: PropertySubType): boolean {
    return this.subTypeSelection.isSelected(pst.id);
  }

  public toggleAllTypes(checked: boolean) {
    if (checked) this.subTypeSelection.clear();
  }

  public toggleType(pt: PropertyType) {
    const ids = pt.subTypeList.map(pst => pst.id);
    if (this.isTypeChecked(pt)) this.subTypeSelection.deselect(...ids);
    else this.subTypeSelection.select(...ids);
  }

  public toggleSubType(pst: PropertySubType) {
    this.subTypeSelection.toggle(pst.id);
  }

  /** Fires with the reason the menu closed: falsy = dismissed via outside click -> discard staged changes. */
  public onMenuClosed(reason: any) {
    if (!reason) {
      this.subTypeSelection = new SelectionModel<number>(true, this.committedSubTypeIds);
    }
  }

  public onApplyClick() {
    this.committedSubTypeIds = [...this.subTypeSelection.selected];
    this.emitSelection();
  }

  public onClearClick() {
    this.subTypeSelection.clear();
    this.committedSubTypeIds = [];
    this.emitSelection();
  }

  private emitSelection() {
    if (this.allTypesSelected) {
      this.onPropertyTypeSelectionChange.emit(undefined);
      return;
    }

    const ptArray = this.propertyTypes
      .map(pt => {
        const selectedSubTypes = pt.subTypeList.filter(pst => this.subTypeSelection.isSelected(pst.id));
        if (!selectedSubTypes.length) return null;

        const result = new PropertyType();
        result.id = pt.id;
        result.name = pt.name;
        result.subTypeList = selectedSubTypes;
        result.subTypeIdList = selectedSubTypes.map(pst => pst.id);
        return result;
      })
      .filter((pt): pt is PropertyType => pt !== null);

    this.onPropertyTypeSelectionChange.emit(ptArray);
  }
}